// Refactored From https://github.com/pushtell/react-bootstrap-date-picker/blob/master/src/index.jsx to work with React v16

export type DateValues = {
  value?: string
  displayDate: Date
  selectedDate?: Date
  inputValue?: string
}

export const makeInputValueString = (date:Date, dateFormat:string, separator:string) => {
  const month = date.getMonth() + 1;
  const day = date.getDate();

  if (dateFormat.match(/MM.DD.YYYY/)) {
    return (month > 9 ? month : `0${month}`) + separator + (day > 9 ? day : `0${day}`) + separator + date.getFullYear();
  }
  else if (dateFormat.match(/DD.MM.YYYY/)) {
    return (day > 9 ? day : `0${day}`) + separator + (month > 9 ? month : `0${month}`) + separator + date.getFullYear();
  }
  return date.getFullYear() + separator + (month > 9 ? month : `0${month}`) + separator + (day > 9 ? day : `0${day}`);
}

export const makeDateValues = (isoString:string|undefined, dateFormat:string, minDate:Date|undefined, maxDate:Date|undefined, separator:string, utc:boolean) => {
  let selectedDate, inputValue, displayDate;
  if (isoString) {
    const d = (utc ? new Date(isoString).toISOString() : toLocalISOString(isoString)).slice(0,10)
    selectedDate = new Date(d + 'T12:00:00.000Z')
    inputValue = makeInputValueString(selectedDate as Date, dateFormat, separator)
  }
  if (selectedDate) {
    displayDate = new Date(selectedDate);
  } else {
    const today = new Date(`${(new Date().toISOString().slice(0,10))}T12:00:00.000Z`);
    if (minDate && minDate.getTime() >= today.getTime()){
      displayDate = minDate;
    } else if (maxDate && maxDate.getTime() <= today.getTime()){
      displayDate = maxDate;
    } else {
      displayDate = today;
    }
  }

  return {
    value: selectedDate ? selectedDate.toISOString() : undefined,
    displayDate,
    selectedDate,
    inputValue
  } as DateValues;
}

export const toLocalISOString = (date:Date|string) => {
  if (typeof date === 'string') date = new Date(date)
  const tzo = -date.getTimezoneOffset()
  const dif = tzo >= 0 ? '+' : '-'
  const pad = function(num:number, digits=2) {
    const n = Math.pow(10,digits)
    if (num >= n) return num.toString()
    return (num+n).toString().slice(1)
  }

  return date.getFullYear() +
    '-' + pad(date.getMonth() + 1) +
    '-' + pad(date.getDate()) +
    'T' + pad(date.getHours()) +
    ':' + pad(date.getMinutes()) +
    ':' + pad(date.getSeconds()) +
    dif + pad(Math.floor(Math.abs(tzo) / 60)) +
    ':' + pad(Math.abs(tzo) % 60)
}

export const toStartOfDay = (d:Date|string, utc=false) => {
  if (utc) return toLocalISOString(d).slice(0,10) + 'T00:00:00.000Z'
  d = new Date(d)
  d.setHours(0)
  d.setMinutes(0)
  d.setSeconds(0)
  d.setMilliseconds(0)
  return toLocalISOString(d)
}
export const toEndOfDay = (d:Date|string, utc=false) => {
  if (utc) return toLocalISOString(d).slice(0,10) + 'T23:59:59.999Z'
  d = new Date(d)
  d.setHours(23)
  d.setMinutes(59)
  d.setSeconds(59)
  d.setMilliseconds(999)
  return toLocalISOString(d)
}

// ---------------------------------------------------------------------------
// prism-datepicker payload handling
// ---------------------------------------------------------------------------

// prism-datepicker fires prismChange *twice* for a single pick, and the two events do not
// carry the same payload (measured in design-lab/datepicker-probe.html):
//
//   1. { name: 'date-selected', value: '03/14/2026',
//        formattedValue: { single: '2026-03-14T00:00:00-06:00' } }
//   2. { name: 'value-changed', value: '03/14/2026' }   <- no formattedValue at all
//
// Reading only formattedValue.single therefore resolves to "" on the second event and
// erases the day that was just chosen. `value` is the one field present on both, so it is
// the reliable source and formattedValue is only a fallback. Both events resolve to the
// same day, which makes the handler idempotent.
export const toIsoDate = (value:string|undefined) => {
  const raw = (value || '').trim()
  if (!raw) return ''
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10)
  const match = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/)
  return match ? `${match[3]}-${match[1].padStart(2, '0')}-${match[2].padStart(2, '0')}` : ''
}

export interface DatepickerDetail {
  value?: string
  formattedValue?: { single?: string }
}

export const dayFromDatepicker = (detail:DatepickerDetail|undefined) =>
  toIsoDate(detail?.value) || toIsoDate(detail?.formattedValue?.single)

// Renders a stored instant as MM/DD/YYYY for the picker's display value. Mirrors
// makeDateValues: the day is read in UTC when `utc` is set, otherwise in local time.
export const isoToDisplayDate = (isoValue:string|null|undefined, utc=false) => {
  if (!isoValue) return ''
  const d = new Date(isoValue)
  if (isNaN(d.getTime())) return ''
  const [year, month, day] = (utc ? d.toISOString() : toLocalISOString(d)).slice(0,10).split('-')
  return `${month}/${day}/${year}`
}